import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Hero3D from '../Hero3D';
import { Marquee, useReveal } from '../shell';
import { bars, metrics, profile, roles, stack, terminal } from '../data';

/**
 * Typewriter, matching the design's `tick()`: 120ms per character forward,
 * 4.2s hold, 55ms per character back, 700ms between phrases.
 *
 * A single chained timeout rather than an interval, so the forward and reverse
 * speeds can differ without the timer drifting.
 */
const useTypewriter = (phrases) => {
  const [text, setText] = useState('');
  const state = useRef({ idx: 0, chars: 0, deleting: false });

  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setText(phrases[0]);
      return undefined;
    }

    let timer;
    const step = () => {
      const s = state.current;
      const phrase = phrases[s.idx];

      if (!s.deleting && s.chars < phrase.length) {
        s.chars += 1;
        setText(phrase.slice(0, s.chars));
        timer = setTimeout(step, 120);
      } else if (!s.deleting) {
        s.deleting = true;
        timer = setTimeout(step, 4200);
      } else if (s.chars > 0) {
        s.chars -= 1;
        setText(phrase.slice(0, s.chars));
        timer = setTimeout(step, 55);
      } else {
        s.deleting = false;
        s.idx = (s.idx + 1) % phrases.length;
        timer = setTimeout(step, 700);
      }
    };

    timer = setTimeout(step, 300);
    return () => clearTimeout(timer);
  }, [phrases]);

  return text;
};

const Hero = () => {
  const role = useTypewriter(roles);

  return (
    <section className="m-hero">
      <div className="m-hero__text">
        <div className="m-eyebrow">{profile.kicker}</div>

        <h1 className="m-hero__title">
          {profile.first}
          <br />
          {profile.last}
        </h1>

        <div className="m-hero__role">
          <span style={{ color: 'var(--color-accent)' }}>/</span>
          <span>{role}</span>
          <span className="m-caret">_</span>
        </div>

        <p className="m-hero__intro">{profile.intro}</p>

        <div className="m-hero__stack">
          {stack.map((t) => (
            <div key={t} className="m-hero__chip">
              {t}
            </div>
          ))}
        </div>

        <div className="m-hero__actions">
          <a
            className="m-btn m-btn--primary"
            href={profile.resume}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download résumé
          </a>
          <Link className="m-btn m-btn--secondary" to="/projects">
            See the work
          </Link>
        </div>
      </div>

      <div className="m-hero__visual">
        <Hero3D />
        <div className="m-hero__portrait">
          <span className="m-hero__portrait-block" aria-hidden="true" />
          <img src={profile.photo} alt={profile.name} width="1024" height="1024" />
        </div>
      </div>
    </section>
  );
};

const Metrics = () => (
  <section className="m-metrics">
    {metrics.map((m) => (
      <div key={m.label} className="m-metric m-reveal">
        <div className="m-metric__value">{m.value}</div>
        <div className="m-metric__label">{m.label}</div>
      </div>
    ))}
  </section>
);

const Terminal = () => (
  <div className="m-term m-reveal">
    <div className="m-term__bar">
      <span className="m-term__dot m-term__dot--accent" />
      <span className="m-term__dot" />
      <span className="m-term__dot" />
      <span className="m-term__host">devesh@portfolio:~$</span>
    </div>
    <div className="m-term__body">
      {terminal.map((line, i) =>
        line.prompt ? (
          <div key={i}>
            <span style={{ color: 'var(--m-term-accent)' }}>{line.prompt}</span> {line.text}
          </div>
        ) : (
          <div key={i} style={{ opacity: 0.72 }}>
            {line.out}
          </div>
        )
      )}
      <div>
        <span style={{ color: 'var(--m-term-accent)' }}>$</span> <span className="m-caret">_</span>
      </div>
    </div>
  </div>
);

const Bars = () => (
  <div className="m-bars">
    {bars.map((b) => (
      <div key={b.label} className="m-reveal">
        <div className="m-bar__head">
          <span>{b.label}</span>
          <span style={{ color: 'var(--color-neutral-700)' }}>{b.value}%</span>
        </div>
        <div className="m-bar__track">
          <div className="m-bar__fill" style={{ width: `${b.value}%` }} />
        </div>
      </div>
    ))}
  </div>
);

const Home = () => {
  useReveal([]);

  return (
    <>
      <Hero />
      <Marquee />
      <Metrics />

      <section className="m-split">
        <div className="m-split__cell">
          <div className="m-split__kicker">01 / Terminal</div>
          <h2 className="m-split__title">
            Full stack,
            <br />
            front to back
          </h2>
          <Terminal />
        </div>
        <div className="m-split__cell m-split__cell--last">
          <div className="m-split__kicker">02 / Capability</div>
          <h2 className="m-split__title">The arsenal</h2>
          <Bars />
        </div>
      </section>
    </>
  );
};

export default Home;
